import { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { rehydrateDoc } from './docState';
import { useLanguage } from '../../contexts/LanguageContext';

// Shown when a document's answers never loaded.
//
// That state is invisible without it, and expensive. The stores read their defaults, so
// the reader draws a manual nobody has ever run — the same empty screen a new manual
// gives — and nothing done to it is saved (writeState refuses for a document that never
// hydrated, or the first blank default would be written over the real answers).
//
// The cost of leaving it silent is not a lost keystroke. It is a phase launched on that
// screen: the calls go out, the model is paid, the answers arrive, and the refresh after
// them keeps none of it. So the banner says the two things that matter — nothing will be
// saved, and here is the button that fixes it — and it sits above the phases rather than
// inside one, because it is true of all of them.
export function UnsavedBanner({ docId, onRecovered }: { docId: string; onRecovered: () => void }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  // A retry that fails again leaves the banner exactly as it was, which already says the
  // right thing; this only stops the spinner from reading as "it worked".
  const [failedAgain, setFailedAgain] = useState(false);

  const retry = async () => {
    setBusy(true);
    setFailedAgain(false);
    try {
      if (await rehydrateDoc(docId)) onRecovered();
      else setFailedAgain(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-center">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <p className="text-sm font-medium text-amber-800">
        {t('notice.state.unsaved')}
      </p>
      <button
        type="button"
        onClick={retry}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-lg border border-amber-500 px-3 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        {t('notice.state.retry')}
      </button>
      {failedAgain && !busy && (
        <span className="text-xs text-amber-700">
          {t('notice.state.stillDown')}
        </span>
      )}
    </div>
  );
}
